import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Users, FileText, AlertTriangle, ListChecks, Sparkles, ArrowRight,
  TrendingUp, Wallet, Receipt, Bell
} from "lucide-react";
import { useAuth, can } from "@/lib/auth";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const COLORS = {
  blue: "#002FA7",
  cyan: "#1ABEDC",
  green: "#039855",
  red: "#D92D20",
  amber: "#F79009",
  purple: "#7C3AED",
  ink: "#18181B",
};

const PIE_COLORS = [COLORS.blue, COLORS.cyan, COLORS.amber, COLORS.green, COLORS.red, COLORS.purple, "#94A3B8", "#0EA5E9"];

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const KPI = ({ label, value, accent, icon: Icon, testid, sub }) => (
  <div className="bg-white border border-zinc-200 p-5 relative overflow-hidden" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="label-caps mb-2 text-[10px]">{label}</div>
        <div className={`font-display font-black text-3xl tracking-tighter ${accent || "text-zinc-900"}`}>
          {value}
        </div>
        {sub && <div className="text-xs text-zinc-500 mt-1.5">{sub}</div>}
      </div>
      {Icon && (
        <div className="w-9 h-9 bg-zinc-50 border border-zinc-200 flex items-center justify-center">
          <Icon size={16} className="text-zinc-700" />
        </div>
      )}
    </div>
  </div>
);

const Section = ({ title, subtitle, children, testid, right }) => (
  <div className="bg-white border border-zinc-200" data-testid={testid}>
    <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
      <div>
        <p className="label-caps text-[10px]">{subtitle}</p>
        <h3 className="font-display font-bold text-base tracking-tight mt-0.5">{title}</h3>
      </div>
      {right}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [insights, setInsights] = useState("");
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [reminders, setReminders] = useState(null);
  const [loadingReminders, setLoadingReminders] = useState(false);

  const canDash = can(user, "dashboard_view");

  const load = async () => {
    const [s, a] = await Promise.all([
      api.get("/dashboard/stats"),
      api.get("/analytics/overview?months=6"),
    ]);
    setStats(s.data);
    setAnalytics(a.data);
  };

  useEffect(() => { if (canDash) load(); }, [canDash]);

  if (!canDash) {
    if (can(user, "invoices_view") || can(user, "invoices_write")) return <Navigate to="/invoices" replace />;
    if (can(user, "clients_view")) return <Navigate to="/clients" replace />;
    if (can(user, "tasks_view")) return <Navigate to="/tasks" replace />;
    return <Navigate to="/settings" replace />;
  }  const fetchInsights = async () => {
    setLoadingInsights(true);
    setInsights("");
    try {
      const { data } = await api.get("/ai/insights");
      setInsights(data.insights);
    } catch (e) {
      setInsights("Could not load insights. Try again.");
    } finally { setLoadingInsights(false); }
  };

  const fetchReminders = async () => {
    setLoadingReminders(true);
    try {
      const { data } = await api.get("/ai/smart-reminders");
      setReminders(data);
    } catch (e) {
      setReminders({ reminders: [], summary: "Could not load reminders." });
    } finally { setLoadingReminders(false); }
  };

  if (!stats || !analytics) {
    return <div className="p-8 font-mono text-sm text-zinc-500">[ loading dashboard... ]</div>;
  }

  const a = analytics;
  const collected = a.revenue_trend.reduce((s, r) => s + r.collected, 0);
  const billed = a.revenue_trend.reduce((s, r) => s + r.billed, 0);
  const expenses6m = a.revenue_trend.reduce((s, r) => s + r.expenses, 0);
  const profit6m = collected - expenses6m;

  return (
    <div className="p-6 md:p-8 max-w-[1600px]">
      {/* Header */}
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-caps mb-2">Console</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-zinc-900">
            Good day, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-zinc-600 mt-2">Here&apos;s the state of your practice — visual summary across all modules.</p>
        </div>
        <div className="font-mono text-xs text-zinc-500 hidden md:block">
          [{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}]
        </div>
      </div>

      {/* North star KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <KPI label="Active Clients" value={stats.total_clients} icon={Users} testid="kpi-clients" />
        <KPI label="Pending Returns" value={stats.pending_returns} icon={FileText} testid="kpi-pending" />
        <KPI label="Overdue Returns" value={stats.overdue_returns}
             accent={stats.overdue_returns > 0 ? "text-[#D92D20]" : ""}
             icon={AlertTriangle} testid="kpi-overdue" />
        <KPI label="Outstanding" value={fmtINR(stats.outstanding_amount)} icon={Receipt} testid="kpi-outstanding" />
        <KPI label="Open Tasks" value={stats.open_tasks} icon={ListChecks} testid="kpi-tasks" />
        <KPI label="Net Profit · 6m" value={fmtINR(profit6m)}
             accent={profit6m >= 0 ? "text-[#039855]" : "text-[#D92D20]"}
             icon={TrendingUp} testid="kpi-profit" />
      </div>

      {/* AI insights + reminders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <div className="bg-[#EFF4FF] border border-[#002FA7] p-5" data-testid="ai-insights-card">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={14} className="text-[#002FA7]" />
            <p className="label-caps text-[#002FA7]">AI Insight · GPT-5.2</p>
          </div>
          <h3 className="font-display font-bold text-lg tracking-tight mb-3 text-zinc-900">Practice intelligence</h3>
          {insights ? (
            <pre className="text-sm whitespace-pre-wrap font-sans text-zinc-800 leading-relaxed max-h-56 overflow-y-auto scrollbar-thin">{insights}</pre>
          ) : (
            <p className="text-sm text-zinc-600">Generate AI insights based on workload, overdue items and outstanding payments.</p>
          )}
          <button
            onClick={fetchInsights}
            disabled={loadingInsights}
            data-testid="generate-insights"
            className="mt-3 bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-blue-800 transition-all flex items-center gap-2 disabled:opacity-60"
          >
            {loadingInsights ? (
              <><span className="font-mono"><span className="ai-loading-dot">.</span><span className="ai-loading-dot">.</span><span className="ai-loading-dot">.</span></span> Analysing</>
            ) : (
              <><Sparkles size={14} /> {insights ? "Regenerate" : "Generate"} insights</>
            )}
          </button>
        </div>

        <div className="bg-zinc-950 text-white p-5" data-testid="smart-reminders-card">
          <div className="flex items-center gap-2 mb-1">
            <Bell size={14} className="text-[#1ABEDC]" />
            <p className="label-caps text-[#1ABEDC]">Smart Reminders</p>
          </div>
          <h3 className="font-display font-bold text-lg tracking-tight mb-3">Chase list (AI-prioritised)</h3>
          {reminders ? (
            <>
              <p className="text-sm text-zinc-300 leading-relaxed mb-3">{reminders.summary}</p>
              {reminders.reminders.length > 0 && (
                <div className="space-y-1.5 max-h-48 overflow-y-auto scrollbar-thin">
                  {reminders.reminders.slice(0, 6).map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs border-b border-zinc-800 pb-1.5">
                      <span className={`label-caps text-[9px] px-1.5 py-0.5 ${r.type === "invoice" ? "bg-[#FFFAEB] text-[#B54708]" : "bg-[#FEF3F2] text-[#B42318]"}`}>{r.type}</span>
                      <span className="font-semibold text-white">{r.client_name}</span>
                      <span className="text-zinc-400 font-mono">{r.ref}</span>
                      <span className="ml-auto text-[#F79009]">{r.days_overdue}d overdue</span>
                      {r.amount > 0 && <span className="text-white font-semibold">{fmtINR(r.amount)}</span>}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-zinc-400">Pull AI-prioritised list of overdue invoices and tax returns.</p>
          )}
          <button
            onClick={fetchReminders}
            disabled={loadingReminders}
            data-testid="generate-reminders"
            className="mt-3 bg-[#1ABEDC] text-zinc-950 px-4 py-2 text-sm font-semibold hover:bg-cyan-400 flex items-center gap-2 disabled:opacity-60"
          >
            {loadingReminders ? "Loading..." : <><Bell size={14} /> {reminders ? "Refresh" : "Load reminders"}</>}
          </button>
        </div>
      </div>

      {/* Charts row 1: revenue trend (line) + invoice status pie */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <div className="lg:col-span-2">
          <Section title="Revenue Trend · last 6 months" subtitle="Money flow" testid="chart-revenue-trend">
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={a.revenue_trend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="billedGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="collectedGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.green} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.green} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="billed" stroke={COLORS.blue} fillOpacity={1} fill="url(#billedGrad)" strokeWidth={2} name="Billed" />
                <Area type="monotone" dataKey="collected" stroke={COLORS.green} fillOpacity={1} fill="url(#collectedGrad)" strokeWidth={2} name="Collected" />
                <Line type="monotone" dataKey="expenses" stroke={COLORS.red} strokeWidth={2} dot={{ r: 3 }} name="Expenses" />
              </AreaChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-3 gap-2 mt-3 text-center">
              <div className="border border-zinc-200 p-2">
                <div className="text-[10px] label-caps">Billed</div>
                <div className="font-display font-bold text-base text-[#002FA7]">{fmtINR(billed)}</div>
              </div>
              <div className="border border-zinc-200 p-2">
                <div className="text-[10px] label-caps">Collected</div>
                <div className="font-display font-bold text-base text-[#039855]">{fmtINR(collected)}</div>
              </div>
              <div className="border border-zinc-200 p-2">
                <div className="text-[10px] label-caps">Expenses</div>
                <div className="font-display font-bold text-base text-[#D92D20]">{fmtINR(expenses6m)}</div>
              </div>
            </div>
          </Section>
        </div>

        <Section title="Invoice Status" subtitle="Distribution" testid="chart-invoice-status">
          {a.invoice_status.length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-sm text-zinc-500 font-mono">[ no data ]</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={a.invoice_status} dataKey="value" nameKey="name" cx="50%" cy="50%"
                     outerRadius={85} innerRadius={45} label={(e) => `${e.name}: ${e.value}`}>
                  {a.invoice_status.map((entry, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Section>
      </div>

      {/* Charts row 2: returns by type + expense category + top outstanding */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <Section title="Returns by Type" subtitle="Compliance load" testid="chart-returns-by-type">
          {a.returns_by_type.length === 0 ? (
            <div className="h-[230px] flex items-center justify-center text-sm text-zinc-500 font-mono">[ no returns yet ]</div>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={a.returns_by_type} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="value" fill={COLORS.blue} name="Returns" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>

        <Section title="Expenses by Category" subtitle="Where money goes" testid="chart-expense-categories">
          {a.expense_categories.length === 0 ? (
            <div className="h-[230px] flex items-center justify-center text-sm text-zinc-500 font-mono">[ no expenses logged ]</div>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <PieChart>
                <Pie data={a.expense_categories} dataKey="value" nameKey="name" cx="50%" cy="50%"
                     outerRadius={80} innerRadius={40} paddingAngle={2}>
                  {a.expense_categories.map((entry, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Section>

        <Section title="Top 6 Outstanding" subtitle="Chase these clients" testid="chart-top-outstanding">
          {a.top_outstanding.length === 0 ? (
            <div className="h-[230px] flex items-center justify-center text-sm text-zinc-500 font-mono">[ no outstanding ]</div>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart layout="vertical" data={a.top_outstanding} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={100} />
                <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="value" fill={COLORS.red} name="Outstanding ₹" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>
      </div>

      {/* Charts row 3: client constitution + tasks status + whatsapp activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <Section title="Clients by Constitution" subtitle="Portfolio mix" testid="chart-client-constitution">
          {a.client_constitution.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-sm text-zinc-500 font-mono">[ no clients yet ]</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={a.client_constitution} dataKey="value" nameKey="name" cx="50%" cy="50%"
                     outerRadius={70} label={(e) => `${e.name}`} labelLine={false}>
                  {a.client_constitution.map((entry, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Section>

        <Section title="Tasks status" subtitle="Workload" testid="chart-tasks-status">
          {a.tasks_status.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-sm text-zinc-500 font-mono">[ no tasks yet ]</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={a.tasks_status} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="value" fill={COLORS.cyan} name="Tasks" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>

        <Section title="WhatsApp activity · last 7d" subtitle="Outreach" testid="chart-whatsapp-activity">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={a.whatsapp_activity} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="messages" stroke="#25D366" strokeWidth={2.5} dot={{ r: 4 }} name="Messages" />
            </LineChart>
          </ResponsiveContainer>
        </Section>
      </div>

      {/* Upcoming returns table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-zinc-200" data-testid="upcoming-returns">
          <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <p className="label-caps text-[10px]">Next 30 days</p>
              <h3 className="font-display font-bold text-base tracking-tight mt-0.5">Upcoming Returns</h3>
            </div>
            <Link to="/returns" className="text-xs font-semibold text-zinc-900 underline underline-offset-4 hover:text-[#002FA7] flex items-center gap-1">
              All returns <ArrowRight size={12} />
            </Link>
          </div>
          {stats.upcoming_returns?.length ? (
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left px-5 py-2 label-caps text-[10px]">Client</th>
                  <th className="text-left px-5 py-2 label-caps text-[10px]">Type</th>
                  <th className="text-left px-5 py-2 label-caps text-[10px]">Period</th>
                  <th className="text-left px-5 py-2 label-caps text-[10px]">Due</th>
                </tr>
              </thead>
              <tbody>
                {stats.upcoming_returns.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                    <td className="px-5 py-2 text-sm font-medium">{r.client_name}</td>
                    <td className="px-5 py-2 text-sm font-mono">{r.return_type}</td>
                    <td className="px-5 py-2 text-sm">{r.period}</td>
                    <td className="px-5 py-2 text-sm">{r.due_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-10 text-center text-sm text-zinc-500 font-mono">[ nothing in the queue ]</div>
          )}
        </div>

        <div className="bg-zinc-900 text-white p-5">
          <p className="label-caps text-zinc-400 mb-2">Quick action</p>
          <h4 className="font-display font-bold text-lg tracking-tight mb-3">Start a new filing</h4>
          <Link to="/returns" className="text-sm font-semibold underline underline-offset-4 hover:text-white/80 flex items-center gap-1 mb-4">
            Add return <ArrowRight size={14} />
          </Link>
          <div className="border-t border-zinc-700 pt-4">
            <div className="flex items-center gap-2 text-zinc-300 mb-1"><Wallet size={14} /><span className="text-xs label-caps text-zinc-400">Team</span></div>
            <div className="font-display font-bold text-2xl">{stats.total_users}</div>
            {user?.role === "admin" && (
              <Link to="/team" className="text-xs font-semibold mt-2 inline-block underline underline-offset-4 text-zinc-300">Manage team →</Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
